/**
 * The one place the app talks to the network.
 *
 * Contract: `040-api-contract.md` (error envelope, the closed code set),
 * `050-auth.md` §"Frontend behaviour" (credentials, CSRF, the 401 and 403 paths).
 *
 * Three rules that must not be relaxed:
 *
 *  1. `credentials: 'same-origin'` on EVERY request. Without it `fetch` does not
 *     send the session cookie and you will spend an hour on it.
 *  2. The CSRF token lives in memory ONLY — never localStorage, never a cookie.
 *     The whole point of the httpOnly session cookie (adr-0008) is that a script
 *     injected through a paste cannot walk off with a credential; a CSRF token in
 *     localStorage hands it back a piece of one.
 *  3. A 403 refreshes the token from `/api/auth/me` and retries the request
 *     EXACTLY ONCE. Never a loop.
 */

import { getLang } from '@/lib/i18n';

/** The closed set from `040-api-contract.md`. Do not invent codes. */
export type ErrorCode =
  | 'bad_request'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'stale_write'
  | 'payload_too_large'
  | 'unsupported_media_type'
  | 'validation_failed'
  | 'rate_limited'
  | 'server_error'
  | 'upstream_error';

export interface Violation {
  field: string;
  message: string;
}

interface ErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    violations?: Violation[];
  };
}

const ERROR_CODES: ReadonlySet<string> = new Set<ErrorCode>([
  'bad_request',
  'unauthenticated',
  'forbidden',
  'not_found',
  'conflict',
  'stale_write',
  'payload_too_large',
  'unsupported_media_type',
  'validation_failed',
  'rate_limited',
  'server_error',
  'upstream_error',
]);

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode;
  /** Present only on 422 (`040-api-contract.md`). */
  readonly violations: Violation[] | undefined;
  /** Seconds, from the `Retry-After` header. Present on 429. */
  readonly retryAfter: number | undefined;
  /** The raw payload — keeps `current` (412) and `budget` (AI 422) reachable. */
  readonly body: unknown;

  // Fields rather than parameter properties: `erasableSyntaxOnly` is on (tsconfig.app.json).
  constructor(
    status: number,
    code: ErrorCode,
    message: string,
    violations?: Violation[],
    retryAfter?: number,
    body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.violations = violations;
    this.retryAfter = retryAfter;
    this.body = body;
  }
}

/* ── The CSRF token: in memory, and nowhere else ─────────────────────────── */

let csrfToken: string | null = null;

export function getCsrfToken(): string | null {
  return csrfToken;
}

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/* ── The 401 path ───────────────────────────────────────────────────────────
 *
 * `client.ts` cannot import the router or the QueryClient without a cycle, so the
 * app registers a handler at boot (see `features/auth/SessionBridge.tsx`).
 *
 * When the outbox lands (M2, `112-offline-drafts.md` §9) it hooks in here too —
 * and it PAUSES. It must never drain into the void, and a 401 handler that calls
 * `outbox.clear()` violates the project's first design value.
 */

type UnauthenticatedHandler = () => void;

let onUnauthenticated: UnauthenticatedHandler = () => {};

export function setUnauthenticatedHandler(handler: UnauthenticatedHandler): void {
  onUnauthenticated = handler;
}

/**
 * Run the app's 401 path for a response this module did not make. The SSE stream
 * (`090` §6) is the only caller — it uses raw `fetch`, so `dispatch()` never sees its 401,
 * and without this a dead session would surface as a bare error in the composer instead of
 * a redirect to the login screen.
 */
export function notifyUnauthenticated(): void {
  setCsrfToken(null);
  onUnauthenticated();
}

/* ── The request ────────────────────────────────────────────────────────────*/

export interface ApiFetchOptions extends Omit<RequestInit, 'credentials'> {
  /**
   * Skip the global 401 / 403-refresh interceptors.
   *
   * Used by the three auth calls themselves: `/api/auth/me` legitimately 401s on
   * boot (that is how the app learns it is signed out — it cannot read the
   * httpOnly cookie), and routing to `/login` from inside the boot probe would be
   * a redirect loop with a toast on top of it.
   */
  raw?: boolean;
}

const SAFE_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS']);

function toErrorCode(value: unknown, status: number): ErrorCode {
  if (typeof value === 'string' && ERROR_CODES.has(value)) {
    return value as ErrorCode;
  }
  return status >= 500 ? 'server_error' : 'bad_request';
}

function parseRetryAfter(res: Response): number | undefined {
  const header = res.headers.get('Retry-After');
  if (header === null) return undefined;
  const seconds = Number.parseInt(header, 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : undefined;
}

/**
 * Exported for the ONE request in the app that `apiFetch` cannot make: the AI SSE stream
 * (`090-ai-integration.md` §6). It needs the raw `Response` — `apiFetch` parses the body as
 * JSON, and this body is a `ReadableStream` we must read frame by frame — but a failed send
 * must still produce the same `ApiError` (a 422 carrying `budget`, a 429 carrying
 * `Retry-After`) that every other call site already knows how to render.
 */
export async function toApiError(res: Response): Promise<ApiError> {
  const payload: unknown = await res.json().catch(() => null);
  const envelope = (payload ?? {}) as ErrorEnvelope;

  return new ApiError(
    res.status,
    toErrorCode(envelope.error?.code, res.status),
    envelope.error?.message ?? res.statusText,
    envelope.error?.violations,
    parseRetryAfter(res),
    payload,
  );
}

/**
 * The CSRF refresh, deduplicated.
 *
 * The outbox replays several mutations at once (`112-offline-drafts.md`); if the
 * session's token changed under us, all of them 403 at the same moment. Without
 * this shared promise each would fire its own `/api/auth/me`.
 */
let csrfRefresh: Promise<string | null> | null = null;

function refreshCsrfToken(): Promise<string | null> {
  csrfRefresh ??= (async () => {
    try {
      // NOT `raw`: if this 401s the session is genuinely gone, and the 401 path
      // above is exactly what should run. (The boot probe in `useSession` is the
      // one that passes `raw` — a 401 there is normal, it just means "signed out".)
      const me = await apiFetch<{ csrfToken: string }>('/api/auth/me');
      setCsrfToken(me.csrfToken);
      return me.csrfToken;
    } finally {
      csrfRefresh = null;
    }
  })();

  return csrfRefresh;
}

async function send(path: string, options: ApiFetchOptions): Promise<Response> {
  const { raw: _raw, headers: extra, ...init } = options;
  const method = (init.method ?? 'GET').toUpperCase();

  const headers = new Headers(extra);
  headers.set('Accept', 'application/json');
  // The backend localizes its error envelope from Accept-Language (`100-settings.md`,
  // `LocaleSubscriber`). It mirrors the UI language so a German session gets German errors.
  if (!headers.has('Accept-Language')) headers.set('Accept-Language', getLang());

  // FormData sets its own multipart boundary — never override it (M3's asset upload).
  if (init.body !== undefined && init.body !== null && !(init.body instanceof FormData)) {
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  }

  // 050-auth.md: EVERY non-GET in the entire API requires the token. Not just auth routes.
  if (!SAFE_METHODS.has(method)) {
    const token = getCsrfToken();
    if (token !== null) headers.set('X-CSRF-Token', token);
  }

  return fetch(path, {
    ...init,
    method,
    headers,
    credentials: 'same-origin', // rule 1. Always.
  });
}

async function parse<T>(res: Response): Promise<T> {
  if (res.status === 204 || res.headers.get('Content-Length') === '0') {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  return dispatch<T>(path, options, false);
}

/**
 * `retried` is a private argument, not an option: it is the thing that makes
 * "exactly once" structural. A caller cannot set it, so a retried request can
 * never re-enter the 403 branch, so the retry can never loop.
 */
async function dispatch<T>(path: string, options: ApiFetchOptions, retried: boolean): Promise<T> {
  const res = await send(path, options);

  if (res.ok) return parse<T>(res);

  const method = (options.method ?? 'GET').toUpperCase();

  if (res.status === 401) {
    const error = await toApiError(res);
    setCsrfToken(null); // the session is gone; the token it belonged to is worthless
    if (!options.raw) onUnauthenticated();
    throw error;
  }

  // 050-auth.md §"What the client does on a 403": the session's CSRF token changed
  // underneath us (a second tab signed in again), or the session is gone.
  // Refresh once from /api/auth/me, retry once, and then stop.
  if (res.status === 403 && !options.raw && !retried && !SAFE_METHODS.has(method)) {
    let fresh: string | null;
    try {
      fresh = await refreshCsrfToken();
    } catch (error) {
      // /api/auth/me itself failed. If it 401'd, the session is gone, not merely
      // stale — and the 401 path has already run inside that call. Either way:
      // surface it, do not retry.
      if (error instanceof ApiError) throw error;
      throw await toApiError(res);
    }

    if (fresh !== null) {
      return dispatch<T>(path, options, true);
    }
  }

  throw await toApiError(res);
}
